import { Suspense, lazy, useState } from "react";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Label from "@components/form/Label";
import Card from "@components/layout/Card";
import Layout from "@components/layout/Layout";
import Loading from "@components/shared/Loading";
import { useAllAccountingPeriods } from "@hooks/query/useAllAccountingPeriods";
import { getMeterReadSequenceReports } from "@services/reports";
import { formatDate } from "@utils/formatDate";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";
import { addQuarters, endOfQuarter, endOfYear, startOfQuarter } from "date-fns";

const MeterReadSequenceReport = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [renderers, setRenderers] = useState<any>({});
  const [SelectedReport, setSelectedReport] = useState<any>();
  const [reportProps, setReportProps] = useState<{
    report?: any;
    fromDate?: Date;
    toDate?: Date;
    accountingPeriodStart?: Date;
    accountingPeriodEnd?: Date;
    runAt?: Date;
    level1ResourceId?: string;
  }>({});
  const [filter, setFilter] = useState<{
    level1ResourceId?: string;
    report?: any;
    selectedDateRangeLabel?: string;
    fromDate?: Date;
    toDate?: Date;
    accountingPeriodStart?: Date;
    accountingPeriodEnd?: Date;
  }>({});

  const { data: accountingPeriods = [] } = useAllAccountingPeriods({
    params: {
      level1ResourceId: filter.level1ResourceId,
    },
    options: {
      enabled: Boolean(filter.level1ResourceId),
      refetchOnWindowFocus: false,
    },
  });

  const { data: reports } = useQuery(
    ["reports", "meterReadSequence"],
    getMeterReadSequenceReports,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        const updatedRenderers = { ...renderers };
        for (const report of data) {
          updatedRenderers[report.id] = lazy(
            () => import(`@components/reports/${report.type}`),
          );
        }

        setRenderers(updatedRenderers);
        setSelectedReport(undefined);

        const selectedReport = data.find((r: any) => r.id === id);
        if (selectedReport) {
          setFilter({ ...filter, report: selectedReport });
        }
      },
    },
  );

  const getDateRangeOptions = () => {
    const options = [];

    const uniquePeriods: any = {};
    accountingPeriods?.forEach((period: any) => {
      const uniqueKey = `${period.periodStart.toString()}-${period.periodEnd.toString()}`;
      if (!uniquePeriods[uniqueKey]) {
        uniquePeriods[uniqueKey] = period;
      }
    });

    const uniqueAccountingPeriods: any = Object.values(uniquePeriods);

    if (uniqueAccountingPeriods)
      for (const accountingPeriod of uniqueAccountingPeriods) {
        for (let i = 0; i < 4; i++) {
          const qStart = startOfQuarter(
            addQuarters(new Date(accountingPeriod.periodStart), i),
          );
          const qEnd = endOfQuarter(qStart);

          options.push({
            label: `Q${i + 1} ${new Date(
              accountingPeriod.periodStart,
            ).getFullYear()} (${formatDate(qStart)} - ${formatDate(qEnd)})`,
            value: {
              fromDate: startOfQuarter(
                addQuarters(new Date(accountingPeriod.periodStart), -1),
              ),
              toDate: qEnd,
              accountingPeriodStart: new Date(accountingPeriod.periodStart),
              accountingPeriodEnd: new Date(accountingPeriod.periodEnd),
            },
          });
        }
        const yStart = new Date(accountingPeriod.periodStart);
        const yEnd = endOfYear(yStart);

        options.push({
          label: `Water Year ${new Date(
            accountingPeriod.periodStart,
          ).getFullYear()} (${formatDate(yStart)} - ${formatDate(yEnd)})`,
          value: {
            fromDate: startOfQuarter(addQuarters(yStart, -1)),
            toDate: yEnd,
            accountingPeriodStart: yStart,
            accountingPeriodEnd: yEnd,
          },
        });
      }

    return options;
  };

  const getReportOptions = () => {
    return reports?.map((report: any) => ({
      label: report.name,
      value: report.id,
    }));
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleDateRangeChange = (
    selectedDateRangeLabel: string,
    fromDate: Date,
    toDate: Date,
    accountingPeriodStart: Date,
    accountingPeriodEnd: Date,
  ) => {
    setFilter({
      ...filter,
      selectedDateRangeLabel,
      fromDate,
      toDate,
      accountingPeriodStart,
      accountingPeriodEnd,
    });
  };

  const handleRenderReport = (e: any) => {
    e.preventDefault();

    if (renderers[filter.report?.id]) {
      setSelectedReport(renderers[filter.report?.id]);
      setReportProps({
        report: filter.report,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        accountingPeriodStart: filter.accountingPeriodStart,
        accountingPeriodEnd: filter.accountingPeriodEnd,
        runAt: new Date(),
        level1ResourceId: filter.level1ResourceId,
      });
    }
  };

  return (
    <Layout
      permissions={["ViewReports"]}
      breadcrumb={[
        {
          label: t("dashboard.dashboard"),
          href: "/polestar",
        },
        {
          label: t("reporting.title"),
        },
      ]}
      title={t("reporting.new_meter_read_sequence_report")}
    >
      <Card className="flex flex-col rounded-none p-0 grow">
        <div className="flex flex-col grow gap-4 divide-y">
          <form
            className="items-end gap-2 p-4 grid grid-cols-1 md:gird-cols-2 xl:grid-cols-4"
            onSubmit={handleRenderReport}
          >
            <div className="w-auto">
              <Label htmlFor="report">{t("reporting.filter_report")}</Label>
              <Select
                options={getReportOptions()}
                className="w-full"
                value={getReportOptions()?.find(
                  (e: any) => e.value === filter.report?.id,
                )}
                onChange={(e: any) => {
                  handleFilterChange(
                    "report",
                    reports?.find((report: any) => report.id === e?.value),
                  );
                }}
              />
            </div>
            <div className="w-auto">
              <Label>{t("reporting.filter_level1wrs")}</Label>
              <SelectLevel1Resource
                value={filter?.level1ResourceId}
                onChange={(e: any) => {
                  handleFilterChange("level1ResourceId", e?.value);
                }}
                isClearable
              />
            </div>
            <div className="w-auto">
              <Label htmlFor="from-date">{t("reporting.filter_dates")}</Label>
              <Select
                options={getDateRangeOptions()}
                className="w-full"
                value={getDateRangeOptions()?.find(
                  (e: any) =>
                    e.label === filter.selectedDateRangeLabel &&
                    e.value.fromDate.valueOf() === filter.fromDate?.valueOf() &&
                    e.value.toDate.valueOf() === filter.toDate?.valueOf() &&
                    e.value.accountingPeriodStart.valueOf() ===
                      filter.accountingPeriodStart?.valueOf() &&
                    e.value.accountingPeriodEnd.valueOf() ===
                      filter.accountingPeriodEnd?.valueOf(),
                )}
                onChange={(e: any) => {
                  handleDateRangeChange(
                    e?.label,
                    e?.value.fromDate,
                    e?.value.toDate,
                    e?.value.accountingPeriodStart,
                    e?.value.accountingPeriodEnd,
                  );
                }}
              />
            </div>
            <div className="w-auto">
              <button
                type="submit"
                className="btn-secondary"
                disabled={!filter.report || !filter.fromDate || !filter.toDate}
              >
                {t("reporting.get_data")}
              </button>
            </div>
          </form>
          {SelectedReport && (
            <div className="p-4 flex flex-col grow">
              <Suspense fallback={<Loading />}>
                <SelectedReport {...reportProps} />
              </Suspense>
            </div>
          )}
        </div>
      </Card>
    </Layout>
  );
};

export default MeterReadSequenceReport;
